import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Activity, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_COLORS = {
  POST: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  PUT: 'bg-amber-100 text-amber-700 border-amber-200',
  PATCH: 'bg-amber-100 text-amber-700 border-amber-200',
  DELETE: 'bg-rose-100 text-rose-700 border-rose-200',
};

const formatTs = (ts) => {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return ts; }
};

// Turn a raw "METHOD /api/..." action into a short human label
const prettyAction = (log) => {
  const p = log.path || '';
  const m = log.method;
  if (p.includes('/vendor-shipment/create')) return 'Shipment created';
  if (p.includes('/mark-ready')) return 'Marked Ready for Pickup';
  if (p.includes('/mark-in-transit')) return 'Marked In Transit';
  if (p.includes('/mark-received')) return 'Marked Received';
  if (p.includes('/update-qty')) return 'Quantity updated';
  if (p.includes('/status')) return 'Status changed';
  if (p.includes('/force-draft')) return 'Reverted to Draft';
  if (p.includes('/approve')) return 'Approved';
  return `${m} ${p.replace('/api/', '')}`;
};

export default function ActivityLogButton({ recordId, recordLabel, variant = 'outline', size = 'sm', className = '' }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-logs/record/${recordId}`, { params: { plant_id: 'all' } });
      setLogs(res.data.data || []);
    } catch {
      toast.error('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size={size} variant={variant} onClick={load} title="View Activity"
        className={className} data-testid={`activity-btn-${recordId}`}>
        <Activity className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Activity {recordLabel ? `— ${recordLabel}` : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto pr-1 space-y-3">
            {loading ? (
              <p className="text-center text-slate-400 py-8">Loading…</p>
            ) : logs.length === 0 ? (
              <p className="text-center text-slate-400 py-8" data-testid="activity-empty">No recorded activity for this record yet.</p>
            ) : (
              logs.map((l) => (
                <div key={l.id} className="border rounded-lg p-3 bg-slate-50" data-testid="activity-entry">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${METHOD_COLORS[l.method] || ''}`}>{l.method}</Badge>
                      <span className="font-medium text-slate-800 text-sm">{prettyAction(l)}</span>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{formatTs(l.timestamp)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-600">{l.actor_name}</span>
                    <span className="text-slate-400">·</span>
                    <span className="capitalize">{l.actor_type}{l.actor_role ? ` (${l.actor_role})` : ''}</span>
                    <span className="text-slate-400">·</span>
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Globe className="w-3 h-3" />{l.ip}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span>{l.browser}</span>
                    {l.success === false && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Failed</Badge>}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
